import React, {Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import './SensorReports.scss'
import {toggleModal} from "../../store/actions/toggleModalActions";
import DatePicker from "react-datepicker"
import {
    downloadSensorReportCsv,
    downloadSensorReportPdf,
    getSensorReports,
    getSensorReportsSuccess,
    setSensorReportsFilters
} from "../../store/actions/sensorReportsActions";
import Spinner from "../_common/Spinner";
import ExternalModal from "../_common/ExternalModal";
import SensorListForMeasurement from "./SensorListForMeasurement";
import SensorReportsChart from "./SensorReportsChart";
import {useLocation} from "react-router-dom";


/**
 * Translation map for parameter keys returned by the new API.
 * Maps raw backend keys (e.g. 'temperature') to gettext translation keys.
 */
const getParameterKeyTranslation = (t, parameterKey) => {
    const translations = {
        'temperature': t.gettext('Temperature'),
        'humidity': t.gettext('Humidity'),
        'soil_moisture': t.gettext('Soil Moisture'),
        'soil_conductivity': t.gettext('Soil Conductivity'),
        'ph': t.gettext('pH'),
        'ec': t.gettext('EC'),
        'wind_speed': t.gettext('Wind Speed'),
        'wet_leaf': t.gettext('Wet Leaf'),
        'rain': t.gettext('Rain'),
        'battery': t.gettext('Battery'),
    };
    return translations[parameterKey] || parameterKey;
};

/**
 * Translation map for statistic keys returned by the new API.
 */
const getStatisticKeyTranslation = (t, statisticKey) => {
    const translations = {
        'avg': t.gettext('Average'),
        'min': 'Min',
        'max': 'Max',
        'sum': t.gettext('Total'),
        'minutes': t.gettext('Minutes'),
        'value': '',
    };
    return translations[statisticKey] || statisticKey || '';
};

/**
 * Builds a translated column header from a column object.
 * E.g. { parameterKey: 'temperature', statisticKey: 'avg', unit: 'C' } -> "Temperature Average C"
 */
const getColumnHeader = (t, column) => {
    const paramName = getParameterKeyTranslation(t, column.parameterKey);
    const statName = getStatisticKeyTranslation(t, column.statisticKey);
    const unit = column.unit ? ` ${column.unit}` : '';
    if (!statName) {
        return paramName + unit;
    }
    return `${paramName} ${statName}${unit}`;
};

/**
 * Groups columns by parameterKey for merged table headers.
 * Returns: [{ parameterKey, label, columns: [...] }, ...]
 */
const groupColumnsByParameter = (t, columns) => {
    if (!columns) return [];
    const groups = [];
    const groupMap = {};

    columns.forEach(col => {
        if (!groupMap[col.parameterKey]) {
            groupMap[col.parameterKey] = {
                parameterKey: col.parameterKey,
                label: getParameterKeyTranslation(t, col.parameterKey),
                columns: []
            };
            groups.push(groupMap[col.parameterKey]);
        }
        groupMap[col.parameterKey].columns.push(col);
    });

    return groups;
};


const selector = state => {
    const { language,  modalState, sensorReports } = state;
    return {
        t: language.translator,
        modalState: modalState,
        pending: sensorReports.pending,
        sensorReports: sensorReports.sensorReports,
        filters: sensorReports.filters,
    }
};


const SensorReports = () => {
    const { t, modalState, sensorReports, pending, filters } = useSelector(selector)
    const [currentFilter, setCurrentFilter] = useState(null)
    const [showNumberOfDaysFilter, setShowNumberOfDaysFilter] = useState(false);
    const [showMeasurementsPeriodFilter, setShowMeasurementsPeriodFilter] = useState(false);
    const [selectedSensors, setSelectedSensors] = useState([])
    const [selectedMeasurementFilter, setSelectedMeasurementFilter] = useState(null)
    const dispatch = useDispatch();

    useEffect(()=> {
        dispatch(getSensorReportsSuccess(''))
        dispatch(setSensorReportsFilters({dateFrom: '', dateTo: '', numberOfDaysInPast:  checkStorageForData('numberOfDaysInPast'), aggregationPeriod: checkStorageForData('aggregationPeriod'), withBattery: false, sensorId: '', sensorIds: []}))
        if(checkStorageForData('aggregationPeriod').length > 0) {
            setSelectedMeasurementFilter(checkStorageForData('aggregationPeriod'))
        }
        setSelectedSensors([])
    },[])

    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const sensorId = searchParams.get('sensorId');

        if(sensorId) {
            selectSensors([Number(sensorId)])
        }

    }, [location.search]);


    useEffect(() => {
        setCurrentFilter('')
    },[filters.dateTo])

    useEffect(() => {
        setCurrentFilter('')
    },[filters.dateFrom])

    const selectDate = (date, filter) => {
        if(filters.sensorIds.length > 0) {
            const updatedFilters = {...filters, [filter]: date, numberOfDaysInPast: ''};
            clearStorageData('numberOfDaysInPast');
            dispatch(setSensorReportsFilters(updatedFilters))
            dispatch(getSensorReports(updatedFilters))
        }
        closeFilter()
    }

    const selectSensors = (sensorIds) => {
        dispatch(setSensorReportsFilters({...filters, sensorIds: sensorIds}))
        dispatch(getSensorReports({...filters, sensorIds: sensorIds }))
        setSelectedSensors(sensorIds)
        closeModal()
    }


    const numberOfDays = [
        {label: t.gettext('last 24 hours'), value: 1},
        {label: t.gettext('last week'), value: 7},
        {label: t.gettext('last month'), value: 30},
    ]

    const measurePeriod = [
        {label: t.gettext('Raw'), value: 'RAW'},
        {label: t.gettext('30 minute'), value: 'MIN_30'},
        {label: t.gettext('Hour'), value: 'HOUR'},
        {label: t.gettext('Day'), value: 'DAY'},
        {label: t.gettext('Month'), value: 'MONTH'},
    ]


    const closeModal = () => {
        dispatch(toggleModal(false, ''));
    };


    const expand = (filter) => {
        setCurrentFilter(filter)
    }

    const closeFilter = () => {
        // setCurrentFilter(null)
    }

    const clearFilters = () => {
        clearStorageData('numberOfDaysInPast');
        clearStorageData('aggregationPeriod');
        dispatch(setSensorReportsFilters({dateFrom: '', dateTo: '', numberOfDaysInPast: '', aggregationPeriod:'', withBattery: false, sensorId: '', sensorIds: []}))
        setSelectedSensors([])
        setCurrentFilter('')
        dispatch(getSensorReportsSuccess(''))
    }

    const checkStorageForData = (type) => {
        const localStorageKey = `sensors${type.charAt(0).toUpperCase() + type.slice(1)}`;
        return localStorage.getItem(localStorageKey) || '';
    };

    const clearStorageData = (type) => {
        const localStorageKey = `sensors${type.charAt(0).toUpperCase() + type.slice(1)}`;
        localStorage.removeItem(localStorageKey);
    };

    /**
     * Formats a timestamp string for display in the table.
     * Handles ISO datetime strings like "2025-06-01T12:00:00"
     */
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        return timestamp.split('T').join(' ');
    };

    /**
     * Renders the data table from new API response format.
     * New response has: columns[] and rows[] (instead of old headers[]/values[])
     */
    const renderReportTable = () => {
        if (!sensorReports || !sensorReports.columns || !sensorReports.rows) {
            return <div className='selectInfo'>{t.gettext('Please select sensor')}</div>;
        }

        const columnGroups = groupColumnsByParameter(t, sensorReports.columns);

        return (
            <Fragment>
                <ul className="reportsList">
                    <li className='reportsListHeader'>
                        {columnGroups.map((group, index) => (
                            <div className='subHeader' key={index}>
                                <div className='listHeaderCell'>
                                    {group.label}
                                </div>
                                <div className='listHeaderCell subHeaderCell'>
                                    {/* Date/Hour sub-header for first group */}
                                    {index === 0 && (
                                        <div className='subHeaderDiv' style={{width: "180px"}}>
                                            {t.gettext('Date/Hour')}
                                        </div>
                                    )}
                                    {group.columns.map((col, colIndex) => (
                                        <div className='subHeaderDiv' style={{width: "90px"}} key={colIndex}>
                                            {getStatisticKeyTranslation(t, col.statisticKey)}
                                            {col.unit ? ` ${col.unit}` : ''}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </li>
                    {sensorReports.rows.length > 0 &&
                        [...sensorReports.rows].reverse().map((row, rowIndex) => (
                        <li className="reportsListItem" key={rowIndex}>
                            {/* Date cell */}
                            <div className="listItemCell" style={{width: '180px'}}>
                                {formatTimestamp(row.timestampFrom)}
                            </div>
                            {/* Value cells for each column */}
                            {sensorReports.columns.map((col, colIndex) => (
                                <div className="listItemCell" style={{width: "90px"}} key={colIndex}>
                                    {row.values && row.values[col.key] != null
                                        ? row.values[col.key]
                                        : '-'}
                                </div>
                            ))}
                        </li>
                    ))}
                </ul>

                <ul className="reportsList">


                </ul>
            </Fragment>
        );
    };


    return (
        <div className="weatherReports">
            <div className='weatherReportsHeader'>
                <div><span>{t.gettext('Sensor reports')}</span>
                    {sensorReports && sensorReports.sensorName ? <span className='stationName'>{sensorReports.sensorName}</span> : ''}
                </div>
                <div className="selectStationBtn" onClick={() => dispatch(toggleModal(true, 'Select Sensors'))}>
                    {t.gettext('Select sensor')}
                </div>
            </div>
            <div className="weatherReportsFilters">
                <div className="filterContainer" onClick={() => expand('dateFrom')}>
                    <span className='filterType'>{t.gettext('Date from')}:</span>
                    <span className="selectedFilter">&nbsp;{filters.dateFrom ? filters.dateFrom.toLocaleDateString() : ''}</span>
                    {currentFilter === 'dateFrom' &&
                        <DatePicker
                            // className="addModalInput dateInput"
                            dateFormat="dd/MM/yyyy"
                            selected={filters.dateFrom ? filters.dateFrom : null}
                            inline
                            onChange={(date) => selectDate(date, 'dateFrom')}
                            disabled={filters.sensorIds.length === 0}
                            isClearable={true}
                            // onChange={() => loadFields(selectedFilters)}
                        />
                    }
                </div>
                <div className="filterContainer" onClick={() => expand('dateTo')}>
                    <span className='filterType'>{t.gettext('Date to')}:</span>
                    <span className="selectedFilter">&nbsp;{filters.dateTo ? filters.dateTo.toLocaleDateString() : ''}</span>
                    {currentFilter === 'dateTo' &&
                        <DatePicker
                            // className="addModalInput dateInput"
                            dateFormat="dd/MM/yyyy"
                            selected={filters.dateTo ? filters.dateTo : null}
                            inline
                            onChange={(date) => selectDate(date, 'dateTo')}
                            // onChange={() => loadFields(selectedFilters)}
                        />
                    }
                </div>
                <div
                    className='filterTypeFormSelect'
                    onClick={() => setShowNumberOfDaysFilter(!showNumberOfDaysFilter)}
                >
                    <div className='filterTypeFormSelectText'>
                        {filters.numberOfDaysInPast ? numberOfDays.find(x => x.value.toString() === filters.numberOfDaysInPast.toString())?.label : t.gettext('Number of days')}
                    </div>
                    <i className="material-icons dropDownIcon">
                        {showNumberOfDaysFilter ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    </i>
                    {showNumberOfDaysFilter &&
                        <div className='filterTypeFormSelectOptions'>
                            {numberOfDays.map((item, index) => (
                                <div
                                    className='filterTypeFormSelectOption'
                                    key={index}
                                    onClick={() => {
                                        if(filters.sensorIds.length > 0) {
                                            const updatedFilters = {...filters, dateFrom: '', dateTo: '', numberOfDaysInPast: item.value};
                                            dispatch(setSensorReportsFilters(updatedFilters))
                                            dispatch(getSensorReports(updatedFilters))
                                            localStorage.setItem('sensorsNumberOfDaysInPast', item.value)
                                        }
                                    }}
                                >
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    }
                </div>
                <div
                    className='filterTypeFormSelect'
                    onClick={() => setShowMeasurementsPeriodFilter(!showMeasurementsPeriodFilter)}
                >
                    <div className='filterTypeFormSelectText'>
                        {filters.aggregationPeriod ? measurePeriod.find(x => x.value === filters.aggregationPeriod)?.label : t.gettext('Measurements period')}
                    </div>
                    <i className="material-icons dropDownIcon">
                        {showMeasurementsPeriodFilter ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    </i>
                    {showMeasurementsPeriodFilter &&
                        <div className='filterTypeFormSelectOptions'>
                            {measurePeriod.map((item, index) => (
                                <div
                                    className='filterTypeFormSelectOption'
                                    key={index}
                                    onClick={() => {if(filters.sensorIds.length > 0) {
                                        dispatch(setSensorReportsFilters({...filters, aggregationPeriod: item.value }))
                                        dispatch(getSensorReports({...filters, aggregationPeriod: item.value }))
                                        setSelectedMeasurementFilter(item.value)
                                        localStorage.setItem('sensorsAggregationPeriod', item.value)
                                    }
                                    }}
                                >
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    }
                </div>
                <label className='sensorReportCheckbox'>
                    <input
                        type='checkbox'
                        checked={Boolean(filters.withBattery)}
                        onChange={(event) => {
                            const withBattery = event.target.checked;
                            const updatedFilters = {...filters, withBattery};
                            dispatch(setSensorReportsFilters(updatedFilters));
                            if (filters.sensorIds.length > 0) {
                                dispatch(getSensorReports(updatedFilters));
                            }
                        }}
                    />
                    <span>{t.gettext('Show battery')}</span>
                </label>
                {sensorReports && (
                    <div className={'reportBtnContainer'}>
                        <div className="downloadReportBtn" onClick={() => dispatch(downloadSensorReportCsv(filters))}>
                            {t.gettext('Download CSV')}
                        </div>
                        <div className="downloadReportBtn" onClick={() => dispatch(downloadSensorReportPdf(filters))}>
                            {t.gettext('Download PDF')}
                        </div>
                        <div className="downloadReportBtn"
                             onClick={() => dispatch(toggleModal(true, 'Chart'))}
                        >
                            {t.gettext('Charts')}
                        </div>
                    </div>
                )}
                <div className="clearFiltersBtn" onClick={() => clearFilters()}>
                    <span className='btnTxt'>{t.gettext('Clear')}</span>
                </div>
            </div>
            <div className="weatherReportsBox">
                {pending &&
                    <div className='page'>
                        <Spinner size={300}/>
                    </div>
                }
                {renderReportTable()}


            </div>
            {modalState.flag && modalState.modal === 'Select Sensors' &&
                <ExternalModal
                    closeMethod={closeModal}
                    title={t.gettext('Select sensor')}
                >
                    <SensorListForMeasurement
                        sensorPicker
                        selectSensors={selectSensors}
                    />
                </ExternalModal>
            }
            {modalState.flag && modalState.modal === 'Chart' && (
                <ExternalModal
                    closeMethod={closeModal}
                    title={t.gettext('Chart')}
                    >
                    <SensorReportsChart
                        data={sensorReports}
                        measurementPeriod={selectedMeasurementFilter}
                        />
                </ExternalModal>
            )}
        </div>
    )
}

export default SensorReports
